import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Search } from 'lucide-react';
import { Modal, Input, Select, Textarea } from '../../ui-v2.5';
import { Button } from '../../ui-v2';
import { MODALIDAD, MODALIDADES_SIN_CITA } from './etiquetasEstado';
import { hoyEnMadrid } from './horaMadrid';

/**
 * «Registrar intervención» (24/10): la intervención se hizo por otra vía —presencial, teléfono,
 * remoto, Teams— o, si la persona tenía una cita reservada, se marca ESA como realizada.
 *
 * Dos modos:
 *   · con `persona`: alguien de la campaña. Si responde 409 CITA_VIVA, el modal pasa a «marcar esa
 *     cita» con la cita que dice el servidor, en vez de dejar a medias;
 *   · sin `persona` (ALTA): alguien que aún NO está en la campaña. Se busca con `buscarCandidatos(q)`
 *     (≥3 letras; el servidor sólo devuelve nombre y empresa/negocio de quien no está ya) y se elige
 *     el lote (por defecto el último). Al guardar, el servidor la añade a ese lote y registra la
 *     intervención en una sola transacción, sin avisarle de la campaña.
 *
 * Lo usan el resumen (gestión: elige el técnico entre los de la campaña) y el panel del técnico (sin
 * selector: el servidor lo registra a su nombre). `registrar(datos, personaId)` es la llamada al API.
 * La fecha no puede ser futura (también lo comprueba el servidor). La nota, texto plano.
 *
 * Subtareas (3/11): la misma regla que al cerrar una cita en el panel del técnico —todas hechas, o un
 * motivo—, que aplica el servidor. `subtareas` es el catálogo de la campaña; si se marca una cita viva,
 * se parte de lo que ya tenía hecho (`citaViva.subtareas_hechas`), para no borrar su progreso.
 */
const MOTIVO_MAX = 2000;

const fechaCorta = (s) => (s ? String(s).slice(0, 10).split('-').reverse().join('/') : '—');
const BUSQUEDA_MIN = 3;
const ESPERA_MS = 300;

export default function RegistrarIntervencionModal({
  open, onClose, persona = null, tecnicos = null, citaViva = null, registrar, onHecho,
  buscarCandidatos = null, lotes = [], subtareas = [],
}) {
  const alta = !persona;
  const [cita, setCita] = useState(citaViva);
  const [modalidad, setModalidad] = useState(citaViva ? 'con_cita' : 'telefono');
  const [fecha, setFecha] = useState(hoyEnMadrid());
  const [tecnicoId, setTecnicoId] = useState('');
  const [nota, setNota] = useState('');
  const [hechas, setHechas] = useState(() => new Set());
  const [motivo, setMotivo] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState(null);
  // Alta: a quién, y en qué lote.
  const [texto, setTexto] = useState('');
  const [resultados, setResultados] = useState([]);
  const [buscando, setBuscando] = useState(false);
  const [elegida, setElegida] = useState(null);
  // El id del último lote, un valor simple: quien llama pasa `lotes` como un array nuevo en cada
  // render, y depender del array reiniciaría el modal mientras se escribe.
  const ultimoLoteId = useMemo(() => {
    const u = [...(lotes || [])].sort((a, b) => b.numero - a.numero)[0];
    return u ? String(u.id) : '';
  }, [lotes]);
  const [loteId, setLoteId] = useState('');
  // Igual con la función de búsqueda: se lee de una referencia, no se pone en las dependencias (si
  // no, cada render relanzaría la búsqueda, y cada búsqueda otro render).
  const buscarRef = useRef(buscarCandidatos);
  buscarRef.current = buscarCandidatos;

  // Al abrir, se empieza de cero.
  useEffect(() => {
    if (!open) return;
    setCita(citaViva);
    setModalidad(citaViva ? 'con_cita' : 'telefono');
    setFecha(hoyEnMadrid());
    setTecnicoId(citaViva?.tecnico?.id ? String(citaViva.tecnico.id) : (tecnicos && tecnicos.length === 1 ? String(tecnicos[0].id) : ''));
    setNota('');
    setHechas(new Set(citaViva?.subtareas_hechas || []));
    setMotivo('');
    setError(null);
    setTexto(''); setResultados([]); setElegida(null);
    setLoteId(ultimoLoteId);
  }, [open, citaViva, tecnicos, ultimoLoteId]);

  // Alta: buscar en el servidor al dejar de teclear.
  useEffect(() => {
    if (!open || !alta || !buscarRef.current) return undefined;
    const t = texto.trim();
    if (t.length < BUSQUEDA_MIN) { setResultados([]); return undefined; }
    let cancelado = false;
    const espera = setTimeout(async () => {
      setBuscando(true);
      try {
        const r = await buscarRef.current(t);
        if (!cancelado) setResultados(r);
      } catch (e) {
        if (!cancelado) { setResultados([]); setError(e?.response?.data?.error || 'No se ha podido buscar.'); }
      } finally {
        if (!cancelado) setBuscando(false);
      }
    }, ESPERA_MS);
    return () => { cancelado = true; clearTimeout(espera); };
  }, [open, alta, texto]);

  const modalidades = cita ? ['con_cita', ...MODALIDADES_SIN_CITA] : MODALIDADES_SIN_CITA;
  const faltaTecnico = !!tecnicos && !tecnicoId;
  const faltaAlta = alta && (!elegida || !loteId);
  const totalSubs = (subtareas || []).length;
  const faltanSubtareas = totalSubs > 0 && hechas.size < totalSubs;
  const faltaMotivo = faltanSubtareas && !motivo.trim();

  function alternar(id) {
    setHechas((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  async function enviar() {
    setEnviando(true); setError(null);
    try {
      await registrar({
        modalidad, fecha, nota: nota.trim() || undefined,
        ...(tecnicos ? { tecnico_id: Number(tecnicoId) } : {}),
        ...(cita ? { cita_id: cita.id } : {}),
        ...(alta ? { lote_id: Number(loteId) } : {}),
        ...(totalSubs > 0 ? { subtareas_completadas: Array.from(hechas) } : {}),
        ...(faltanSubtareas ? { motivo: motivo.trim() } : {}),
      }, alta ? elegida.persona_id : persona.id);
      onHecho?.();
      onClose();
    } catch (e) {
      const cuerpo = e?.response?.data || {};
      if (!alta && cuerpo.codigo === 'CITA_VIVA' && cuerpo.cita) {
        setCita(cuerpo.cita);
        // Su progreso guardado: se parte de él, para no borrarlo al marcarla.
        setHechas(new Set(cuerpo.cita.subtareas_hechas || []));
        setModalidad('con_cita');
        if (cuerpo.cita.tecnico_id && tecnicos) setTecnicoId(String(cuerpo.cita.tecnico_id));
        setError('Esta persona tiene una cita reservada: no se crea otra. Puedes marcar esa como realizada.');
      } else {
        setError(cuerpo.error || 'No se ha podido registrar la intervención.');
      }
    } finally {
      setEnviando(false);
    }
  }

  let titulo = 'Registrar intervención sin cita';
  if (cita) titulo = 'Marcar la cita como realizada';
  if (alta) titulo = 'Registrar intervención a alguien de fuera de la campaña';

  return (
    <Modal
      open={open}
      onClose={enviando ? undefined : onClose}
      size="md"
      title={titulo}
      subtitle={alta ? (elegida ? elegida.nombre_completo : 'Se le añade a un lote y se registra, sin avisarle de la campaña') : (persona?.nombre || '')}
      footer={(
        <>
          <Button variant="ghost" onClick={onClose} disabled={enviando}>Cancelar</Button>
          <Button onClick={enviar} disabled={enviando || faltaTecnico || faltaAlta || faltaMotivo || !fecha}>
            {enviando ? 'Guardando…' : (cita ? 'Marcar como realizada' : (alta ? 'Añadir y registrar' : 'Registrar'))}
          </Button>
        </>
      )}
    >
      <div className="space-y-3">
        {alta && (
          <>
            <Input label="Persona" icon={Search} value={texto} placeholder={`Al menos ${BUSQUEDA_MIN} letras del nombre`}
              onChange={(e) => { setTexto(e.target.value); setElegida(null); }} maxLength={100} />
            {texto.trim().length >= BUSQUEDA_MIN && !elegida && (
              <ul className="max-h-48 overflow-y-auto rounded-v25-md border border-v25-line">
                {buscando && <li className="px-3 py-2 text-xs text-v25-ink-500">Buscando…</li>}
                {!buscando && resultados.length === 0 && (
                  <li className="px-3 py-2 text-xs text-v25-ink-500">Nadie que no esté ya en la campaña con ese nombre.</li>
                )}
                {resultados.map((c) => (
                  <li key={c.persona_id}>
                    <button type="button" onClick={() => setElegida(c)}
                      className="w-full px-3 py-2 text-left text-sm text-v25-ink-900 hover:bg-v25-surface-sunken">
                      {c.nombre_completo}
                      {c.organizacion && <span className="ml-2 text-xs text-v25-ink-500">{c.organizacion}</span>}
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <Select label="Lote" value={loteId} onChange={(e) => setLoteId(e.target.value)}>
              {(lotes || []).length === 0 && <option value="">Esta campaña no tiene lotes</option>}
              {[...(lotes || [])].sort((a, b) => a.numero - b.numero).map((l) => (
                <option key={l.id} value={l.id}>{`Lote ${l.numero}${l.nombre ? ` · ${l.nombre}` : ''}`}</option>
              ))}
            </Select>
          </>
        )}
        {cita && (
          <p className="rounded-v25-md border border-v25-line bg-v25-surface-sunken px-3 py-2 text-xs text-v25-ink-700">
            Cita reservada el {fechaCorta(cita.fecha)}{cita.hora_inicio ? ` a las ${cita.hora_inicio}` : ''}.
            Si se hizo por otra vía, elige cómo.
          </p>
        )}
        <Select label="Cómo se hizo" value={modalidad} onChange={(e) => setModalidad(e.target.value)}>
          {modalidades.map((m) => <option key={m} value={m}>{MODALIDAD[m].label}</option>)}
        </Select>
        <Input label="Fecha" type="date" value={fecha} max={hoyEnMadrid()} onChange={(e) => setFecha(e.target.value)} />
        {tecnicos && (
          <Select label="Técnico que la hizo" value={tecnicoId} onChange={(e) => setTecnicoId(e.target.value)}>
            <option value="">Elige un técnico</option>
            {tecnicos.map((t) => <option key={t.id} value={t.id}>{t.nombre_completo || `Persona #${t.id}`}</option>)}
          </Select>
        )}
        {totalSubs > 0 && (
          <div>
            <p className="mb-1.5 text-xs font-medium text-v25-ink-700">Subtareas ({hechas.size}/{totalSubs} hechas)</p>
            <ul className="divide-y divide-v25-line rounded-v25-md border border-v25-line">
              {subtareas.map((s) => (
                <li key={s.id}>
                  <label className="flex cursor-pointer items-start gap-2.5 px-3 py-2 text-sm hover:bg-v25-surface-sunken">
                    <input type="checkbox" className="mt-0.5 h-4 w-4" checked={hechas.has(s.id)} onChange={() => alternar(s.id)} />
                    <span className="min-w-0">
                      <span className="text-v25-ink-900">{s.nombre}</span>
                      {s.descripcion && <span className="block text-xs text-v25-ink-500">{s.descripcion}</span>}
                    </span>
                  </label>
                </li>
              ))}
            </ul>
          </div>
        )}
        {faltanSubtareas && (
          <Textarea label="Motivo para registrarla sin todas las subtareas" rows={2} maxLength={MOTIVO_MAX}
            value={motivo} onChange={(e) => setMotivo(e.target.value)}
            hint={`Faltan ${totalSubs - hechas.size} de ${totalSubs}. Obligatorio.`} />
        )}
        <Textarea label="Nota (opcional)" rows={3} maxLength={1000} value={nota} onChange={(e) => setNota(e.target.value)} />
        {error && <p className="text-xs text-v25-expired-fg">{error}</p>}
      </div>
    </Modal>
  );
}
